interface BaseTableFilterField<T = any> {
    key: string;
    label: string;
    defaultValue?: T;
    helpText?: string;
    size?: 'small' | 'middle' | 'large';
}

export interface TextTableFilterField extends BaseTableFilterField<string> {
    type: 'text';
    placeholder?: string;
}

export interface NumberTableFilterField extends BaseTableFilterField<number | null> {
    type: 'number';
    min?: number;
    max?: number;
    placeholder?: string;
}

export interface NumberRangeTableFilterField extends BaseTableFilterField<{ min: number | null; max: number | null }> {
    type: 'numberRange';
    min: number;
    max: number;
    step?: number;
    labels?: {
        min?: string;
        max?: string;
    };
}

export interface DateTableFilterField extends BaseTableFilterField<string | null> {
    type: 'date';
    format?: string;
    placeholder?: string;
}

export interface DateRangeTableFilterField extends BaseTableFilterField<{ from: string | null; to: string | null }> {
    type: 'dateRange';
    format?: string;
    labels?: {
        from?: string;
        to?: string;
    };
}

export interface SelectTableFilterField extends BaseTableFilterField<string | number | null> {
    type: 'select';
    options: Array<{
        label: string;
        value: string | number;
    }>;
    placeholder?: string;
    allowClear?: boolean;
    showSearch?: boolean;
}

export interface MultiSelectTableFilterField extends BaseTableFilterField<Array<string | number>> {
    type: 'multiSelect';
    options: Array<{
        label: string;
        value: string | number;
    }>;
    placeholder?: string;
    maxCount?: number;
}

export interface CheckboxGroupTableFilterField extends BaseTableFilterField<Array<string | number>> {
    type: 'checkboxGroup';
    options: Array<{
        label: string;
        value: string | number;
        className?: string;
    }>;
    layout?: 'horizontal' | 'vertical';
}

export interface CheckboxTableFilterField extends BaseTableFilterField<boolean> {
    type: 'checkbox';
    checkboxLabel: string;
}

export interface RadioGroupTableFilterField extends BaseTableFilterField<string | number | null> {
    type: 'radioGroup';
    options: Array<{
        label: string;
        value: string | number;
    }>;
    layout?: 'horizontal' | 'vertical';
}

export type TableFilterField =
    | TextTableFilterField
    | NumberTableFilterField
    | NumberRangeTableFilterField
    | DateTableFilterField
    | DateRangeTableFilterField
    | SelectTableFilterField
    | MultiSelectTableFilterField
    | CheckboxGroupTableFilterField
    | CheckboxTableFilterField
    | RadioGroupTableFilterField;

export interface TableFilterConfig {
    fields: TableFilterField[];
    applyButtonText?: string;
    resetButtonText?: string;
    showResultCount?: boolean;
}

export type TableFilterValues = Record<string, any>;

export interface TableFilterModalProps {
    visible: boolean;
    onClose: () => void;
    config: TableFilterConfig;
    values: TableFilterValues;
    onApply: (values: TableFilterValues) => void;
    resultCount?: number;
    loading?: boolean;
}

export type ExtractTableFilterValues<T extends TableFilterConfig> = {
    [K in T['fields'][number]['key']]: Extract<T['fields'][number], { key: K }> extends { defaultValue: infer D }
        ? D
        : any;
};
